/**
 * What the chrome's search box actually does.
 *
 * 🔴 IT DID NOTHING AT ALL, AND IT WAS ON EVERY ADULT SCREEN IN THE PRODUCT.
 * `DashboardShell` drew an `<input type="search" placeholder="Search
 * anything…">` with no handler, no state and no results — the same failure the
 * shell's own notes record for the hamburger that opened no drawer and the
 * calendar chip that opened no menu: *"a control that looks like the way out and
 * is not is worse than no control: the reader stops looking for the real one."*
 *
 * 🔴 IT SEARCHED DESTINATIONS ONLY, AND THAT WAS RIGHT UNTIL IT WAS NOT.
 * The argument recorded here was that searching a school's children needed
 * endpoints that take a query, that this product has none, and that a
 * client-side search over *whatever a screen happened to have fetched* would
 * answer "no results" for a child who exists — worse than no box at all.
 *
 * Every clause of that is still true, and the conclusion no longer follows for
 * ONE tier. `getAllStudents`, `getClasses` and `getSchoolStaff` each return a
 * school's COMPLETE collection in a single call — they are not page slices and
 * they take no filter. An index built over all three cannot produce the false
 * negative the objection was about, because there is nothing it has not seen.
 * See `schoolSearchItems` for the corpus and for what keeps it honest.
 *
 * ⚠️ THE PLATFORM TIER IS STILL DESTINATIONS ONLY, AND FOR THE ORIGINAL REASON.
 * A SUPER_ADMIN's corpus is every child in every school; no browser should hold
 * it and no endpoint will answer a query for it yet. Widening this box to that
 * tier without a server behind it is exactly the false "no results" this file
 * refused in the first place.
 *
 * 🔴 AND IT IS ROLE-SCOPED BY CONSTRUCTION. The caller passes `navFor(role)`, so
 * a teacher typing "schools" is told there is nothing rather than being offered
 * a door that answers 403. `RouteDef.roles` records that this is a courtesy and
 * never the control; the server has been the control since §8.1.
 *
 * 🔴 PURE, AND TESTED, BECAUSE THIS REPO HAS NO DOM RENDERER (OD-60). Ranking
 * written inside a component is ranking no test can reach.
 */

/**
 * What a hit is, which decides how a tie breaks and what the row reads.
 *
 * ⚠️ DESTINATIONS OUTRANK RECORDS AT EQUAL SCORE. Somebody typing "reports"
 * wants the Reports screen, not a child whose class is called Reports; the
 * navigation case is the one the box has always served and the common one.
 */
export type SearchKind = "destination" | "child" | "class" | "teacher";

const KIND_RANK: Readonly<Record<SearchKind, number>> = {
  destination: 0,
  child: 1,
  class: 2,
  teacher: 3,
};

/**
 * A secondary string an item can be found by, and how much finding it there is
 * worth relative to the label.
 *
 * 🔴 WEIGHTED RATHER THAN EQUAL, BECAUSE A MATCH IN A NAME IS NOT A MATCH IN A
 * PARENT'S NAME. Searching "8" should not put every child in Class 8 above the
 * child whose roll number IS 8, and flat fields cannot express that.
 */
export interface SearchField {
  text: string;
  /** 0–1, multiplied into the tier this field reaches. Defaults to 1. */
  weight?: number;
}

export interface SearchItem {
  /** Stable identity — the route id, for the caller to navigate with. */
  id: string;
  /** What the row reads: "Attendance". */
  label: string;
  /** The second line: "Parent", or the path. Never part of the match. */
  hint?: string;
  /**
   * The words a person might type for this that are not in its label.
   *
   * ⚠️ THEY MATCH BUT ARE NEVER SHOWN. "absent" finds Attendance; the row still
   * reads "Attendance", because a result that displays the word you typed rather
   * than the place you are going is a result you have to translate.
   */
  keywords?: readonly string[];
  /** Defaults to `destination`, so every existing caller is unchanged. */
  kind?: SearchKind;
  /** Secondary matchable text — a roll number, a class, a parent's name. */
  fields?: readonly SearchField[];
  /**
   * Where picking this goes, as a hash. Absent means "the id IS a route id",
   * which is how every destination works and how this box began.
   */
  href?: string;
}

export interface SearchHit extends SearchItem {
  score: number;
}

/* ─────────────────────────────────────────────── the tiers ── */

/*
 * 🔴 THE TIERS ARE ORDINAL AND THE GAPS ARE LARGE ON PURPOSE. A prefix match on
 * the label must outrank a mid-word match on a keyword however long either
 * string is — "me" should offer Messages before it offers "assessment" — so the
 * tiers cannot be close enough for a length bonus or a field weight to carry a
 * result across the boundary between two of them.
 *
 * ⚠️ THE TWO FUZZY TIERS SIT BELOW EVERY EXACT ONE, and far enough below that a
 * weighted field cannot lift one past a literal substring match. They exist to
 * answer a query that would otherwise answer NOTHING, never to reorder a query
 * that already had real answers.
 */
const EXACT_LABEL = 1000;
const PREFIX_LABEL = 800;
const WORD_PREFIX = 600;
const KEYWORD_EXACT = 500;
const KEYWORD_PREFIX = 400;
const SUBSTRING = 300;
const KEYWORD_SUBSTRING = 200;
const SUBSEQUENCE = 120;
const TYPO = 60;

/**
 * Fold a string to the form both sides of a comparison are held in.
 *
 * 🔴 IT FOLDS ACCENTS NOW, AND THE OLD NOTE HERE PREDICTED EXACTLY WHEN IT WOULD
 * HAVE TO. It read: *"every label this searches is a route title this product
 * wrote… the day this box searches names people typed — a child, a school — is
 * the day that changes."* That day is this change. A roster carries Zoë and
 * José, and a parent who types "zoe" is not making a mistake — NFD splits the
 * diacritic into its own combining code point so stripping the range leaves the
 * base letter.
 *
 * ⚠️ STILL NO STEMMING. "report" finding "reports" is `startsWith`'s job and it
 * already does it; a stemmer would be a second vocabulary to keep honest across
 * two languages, for a corpus of a few hundred short strings.
 */
export function fold(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/** The query as the words a person actually separated. */
function tokenise(query: string): string[] {
  return fold(query).split(/\s+/).filter((t) => t !== "");
}

/**
 * Is `q` a subsequence of `text`, and how tightly packed is it?
 *
 * 🔴 THIS IS THE "cls2b" → "Class 2B" TIER, AND IT IS THE LOOSEST THING HERE, so
 * it is fenced on three sides rather than one:
 *
 *   · it must start at a WORD BOUNDARY — "las" must not find "Class", because a
 *     query that begins mid-word is a substring query and that tier already ran;
 *   · it needs THREE characters, below which nearly every string matches;
 *   · the matched span must stay COMPACT — the characters may be spread over at
 *     most 2.5× the query's own length. Without that, a seven-character query
 *     finds a forty-character label with its letters scattered across five
 *     words, which is a result nobody can see the logic of.
 *
 * Returns 0 for no match, or a 0–1 density where 1 is a contiguous run.
 */
function subsequenceDensity(text: string, q: string): number {
  if (q.length < 3) return 0;

  /* Anchored at a word start: the loosest tier does not get to begin anywhere. */
  const starts = new Set<number>([0]);
  for (let i = 0; i < text.length - 1; i += 1) {
    if (text[i] === " " || text[i] === "-") starts.add(i + 1);
  }

  let best = 0;
  for (const from of starts) {
    if (text[from] !== q[0]) continue;
    let qi = 0;
    let end = from;
    for (let i = from; i < text.length && qi < q.length; i += 1) {
      if (text[i] === q[qi]) {
        qi += 1;
        end = i;
      }
    }
    if (qi < q.length) continue;
    const span = end - from + 1;
    if (span > q.length * 2.5) continue;
    best = Math.max(best, q.length / span);
  }
  return best;
}

/**
 * Damerau–Levenshtein, stopped as soon as it exceeds `max`.
 *
 * 🔴 BOUNDED, NOT FULL. The unbounded distance between two strings is O(n·m) and
 * this runs over every field of every item on every keystroke. Capping it makes
 * it O(n·max) and — more to the point — a distance of 9 and a distance of 4 are
 * the same answer here: not a typo. Returns `max + 1` for "further than that".
 *
 * ⚠️ IT INCLUDES TRANSPOSITION, which is the difference between Damerau and
 * plain Levenshtein and the reason it is worth writing out. "Attendacne" is one
 * swapped pair, and a plain Levenshtein scores that as two edits — the same as a
 * genuinely different word. Transposition is the single commonest typing error.
 */
function boundedDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  if (a === b) return 0;

  let prev2: number[] = [];
  let prev: number[] = Array.from({ length: b.length + 1 }, (_, j) => j);

  for (let i = 1; i <= a.length; i += 1) {
    /* Scoped to the iteration: only `prev` and `prev2` outlive a row. */
    const row: number[] = [i];
    let least = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(
        (row[j - 1] ?? 0) + 1,
        (prev[j] ?? 0) + 1,
        (prev[j - 1] ?? 0) + cost,
      );
      /* The transposition case: the two characters are simply swapped. */
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        v = Math.min(v, (prev2[j - 2] ?? 0) + 1);
      }
      row[j] = v;
      least = Math.min(least, v);
    }
    /* Every value in this row already exceeds the cap; no later row lowers it. */
    if (least > max) return max + 1;
    prev2 = prev;
    prev = row;
  }
  return prev[b.length] ?? max + 1;
}

/**
 * How many edits a token of this length is allowed to be wrong by.
 *
 * ⚠️ NOTHING UNDER FOUR CHARACTERS IS ALLOWED A TYPO AT ALL. At three, one edit
 * reaches most of the dictionary — "att" would find "add", "art" and "act", and
 * a box that answers a three-letter query with four unrelated screens is noisier
 * than one that answers it with the two that actually start that way.
 */
function typoBudget(len: number): number {
  if (len < 4) return 0;
  if (len <= 6) return 1;
  return 2;
}

/** The strict tiers, over one piece of text. No fuzz. */
function literalScore(text: string, q: string): number {
  if (text === q) return EXACT_LABEL;
  if (text.startsWith(q)) return PREFIX_LABEL;
  /* A word inside the label — "attendance" for "Class attendance". Word starts
     only: a match in the middle of a word is what the substring tier is for. */
  if (text.split(/\s+/).some((w) => w.startsWith(q))) return WORD_PREFIX;
  if (text.includes(q)) return SUBSTRING;
  return 0;
}

/** The two fuzzy tiers, over one piece of text. Only reached when literal fails. */
function fuzzyScore(text: string, q: string): number {
  const density = subsequenceDensity(text, q);
  if (density > 0) return Math.round(SUBSEQUENCE * density);

  const budget = typoBudget(q.length);
  if (budget === 0) return 0;
  /*
   * ⚠️ WORD BY WORD, NOT AGAINST THE WHOLE STRING. "attendence" against "class
   * attendance" is six edits as one string and one edit against its second word,
   * and it is obviously the second thing a person meant.
   */
  for (const w of text.split(/\s+/)) {
    if (Math.abs(w.length - q.length) > budget) continue;
    const d = boundedDistance(w, q, budget);
    if (d <= budget) return Math.round(TYPO * (1 - d / (budget + 1)));
  }
  return 0;
}

/** The best any single token does against one item. */
function scoreToken(item: SearchItem, q: string): number {
  const label = fold(item.label);

  let best = literalScore(label, q);
  if (best >= EXACT_LABEL) return best;

  for (const k of item.keywords ?? []) {
    const kw = fold(k);
    if (kw === q) best = Math.max(best, KEYWORD_EXACT);
    else if (kw.startsWith(q)) best = Math.max(best, KEYWORD_PREFIX);
    else if (kw.includes(q)) best = Math.max(best, KEYWORD_SUBSTRING);
  }

  for (const f of item.fields ?? []) {
    const weight = f.weight ?? 1;
    const hit = literalScore(fold(f.text), q);
    /*
     * ⚠️ THE WEIGHT SCALES WITHIN A TIER AND MUST NOT CROSS ONE. Fields are
     * damped by at most a third (0.67 floor) and the tier gaps are 200 apart, so
     * a weighted field match never outranks the tier above it on the label.
     */
    if (hit > 0) best = Math.max(best, Math.round(hit * (0.67 + 0.33 * weight)));
  }

  /* Fuzz is the last resort — only consulted when nothing literal matched. */
  if (best > 0) return best;

  best = fuzzyScore(label, q);
  for (const f of item.fields ?? []) {
    if (best > 0) break;
    best = Math.round(fuzzyScore(fold(f.text), q) * (f.weight ?? 1));
  }
  return best;
}

/**
 * How well one item answers one query. Higher is better; 0 is "not a match".
 *
 * 🔴 EVERY TOKEN MUST MATCH — AND, NOT OR. "class 2b" means both words, so a
 * class called "Class 2B" answers it and a child called "Classic" does not. An
 * OR would make every extra word a person types WIDEN the result list, which is
 * the opposite of what typing more means everywhere else.
 *
 * ⚠️ THE ITEM'S SCORE IS ITS WEAKEST TOKEN, not the average and not the sum. A
 * two-token query where one token matches the label exactly and the other barely
 * matches a low-weighted field is a weak answer, and averaging would hide that
 * behind the strong half. With one token — every query this box saw before this
 * change — the minimum IS that token's score, so nothing about the old behaviour
 * moves.
 */
export function scoreOne(item: SearchItem, query: string): number {
  const tokens = tokenise(query);
  if (tokens.length === 0) return 0;

  let weakest = Infinity;
  for (const tk of tokens) {
    const s = scoreToken(item, tk);
    if (s === 0) return 0;
    weakest = Math.min(weakest, s);
  }
  return weakest;
}

/**
 * The ranked answer for one query.
 *
 * ⚠️ AN EMPTY QUERY RETURNS NOTHING, NOT EVERYTHING. The box sits in the chrome
 * of every screen; a panel of every destination dropping open on focus is a menu
 * nobody asked for over the page they are reading.
 *
 * ⚠️ TIES BREAK ON KIND FIRST AND THEN THE CALLER'S ORDER, WHICH IS THE
 * SIDEBAR'S. Two equal destinations appear in the order the person already knows
 * them in, rather than alphabetically — the nav list is the mental model this box
 * is searched from — and a destination comes before a record that scored the
 * same, which is the note on `KIND_RANK`.
 */
export function searchItems(
  items: readonly SearchItem[],
  query: string,
  limit = 6,
): SearchHit[] {
  if (tokenise(query).length === 0) return [];

  return items
    .map((item, i) => ({ item, i, score: scoreOne(item, query) }))
    .filter((r) => r.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        KIND_RANK[a.item.kind ?? "destination"] -
          KIND_RANK[b.item.kind ?? "destination"] ||
        a.i - b.i,
    )
    .slice(0, limit)
    .map((r) => ({ ...r.item, score: r.score }));
}
